import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from 'react';
import Map, {
  type MapRef,
  type ViewStateChangeEvent,
  type MapLayerMouseEvent,
} from 'react-map-gl/maplibre';
import type { FillExtrusionLayerSpecification, Map as MapLibreMap } from 'maplibre-gl';
import { useGameStore } from '@/state/gameStore';
import { useViewport } from '@/hooks/useViewport';

export interface MapHandle {
  /** Returns the current center of the map view in {lat, lng}. */
  getCenter: () => { lat: number; lng: number } | null;
  /** Smoothly fly the camera to a position with optional target zoom. */
  flyTo: (target: { lat: number; lng: number }, zoom?: number) => void;
}

const MAP_STYLE_URL = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

function detectBuildingSource(map: MapLibreMap): string | null {
  const style = map.getStyle();
  if (!style?.layers) return null;
  const buildingLayer = style.layers.find(
    (l) => 'source-layer' in l && l['source-layer'] === 'building',
  );
  if (buildingLayer && 'source' in buildingLayer && typeof buildingLayer.source === 'string') {
    return buildingLayer.source;
  }
  return null;
}

function makeBuildingExtrusion(source: string): FillExtrusionLayerSpecification {
  return {
    id: 'building-3d',
    type: 'fill-extrusion',
    source,
    'source-layer': 'building',
    minzoom: 14,
    paint: {
      'fill-extrusion-color': '#1f2937',
      'fill-extrusion-height': [
        'interpolate',
        ['linear'],
        ['zoom'],
        14,
        0,
        15.5,
        ['coalesce', ['get', 'render_height'], ['get', 'height'], 6],
      ],
      'fill-extrusion-base': 0,
      'fill-extrusion-opacity': 0.85,
    },
  };
}

interface MapViewProps {
  onMapClick?: (lngLat: { lng: number; lat: number }) => void;
  crosshair?: boolean;
  children?: ReactNode;
}

export const MapView = forwardRef<MapHandle, MapViewProps>(function MapView(
  { onMapClick, crosshair, children },
  ref,
) {
  const mapRef = useRef<MapRef>(null);
  const mapCenter = useGameStore((s) => s.mapCenter);
  const mapZoom = useGameStore((s) => s.mapZoom);
  const setMapView = useGameStore((s) => s.setMapView);
  const buildings3d = useGameStore((s) => s.uiPrefs.layerToggles.buildings3d);
  const viewport = useViewport();
  const isMobile = viewport === 'mobile';

  useImperativeHandle(ref, () => ({
    getCenter: () => {
      const m = mapRef.current?.getMap();
      if (!m) return null;
      const c = m.getCenter();
      return { lat: c.lat, lng: c.lng };
    },
    flyTo: (target, zoom) => {
      const m = mapRef.current?.getMap();
      if (!m) return;
      m.flyTo({
        center: [target.lng, target.lat],
        zoom: zoom ?? Math.max(m.getZoom(), 14),
        duration: 1500,
        essential: true,
      });
    },
  }));

  const ensureBuildings = useCallback((map: MapLibreMap) => {
    try {
      const source = detectBuildingSource(map);
      if (!source) return;
      if (!map.getLayer('building-3d')) {
        map.addLayer(makeBuildingExtrusion(source));
      }
    } catch (err) {
      console.warn('3D buildings unavailable:', err);
    }
  }, []);

  const handleLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (buildings3d) ensureBuildings(map);
  }, [buildings3d, ensureBuildings]);

  // React to toggle changes
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !map.isStyleLoaded()) return;
    if (buildings3d) {
      ensureBuildings(map);
    } else if (map.getLayer('building-3d')) {
      map.removeLayer('building-3d');
    }
  }, [buildings3d, ensureBuildings]);

  const handleMoveEnd = useCallback(
    (e: ViewStateChangeEvent) => {
      setMapView({ lat: e.viewState.latitude, lng: e.viewState.longitude }, e.viewState.zoom);
    },
    [setMapView],
  );

  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      onMapClick?.({ lng: e.lngLat.lng, lat: e.lngLat.lat });
    },
    [onMapClick],
  );

  return (
    <div className={['relative h-full w-full', crosshair ? 'cursor-crosshair' : ''].join(' ')}>
      <Map
        ref={mapRef}
        mapStyle={MAP_STYLE_URL}
        initialViewState={{
          longitude: mapCenter.lng,
          latitude: mapCenter.lat,
          zoom: mapZoom,
          pitch: 45,
          bearing: 0,
        }}
        onLoad={handleLoad}
        onMoveEnd={handleMoveEnd}
        onClick={handleClick}
        maxPitch={60}
        dragRotate={!isMobile}
        touchZoomRotate={!isMobile}
        doubleClickZoom={true}
        cursor={crosshair ? 'crosshair' : undefined}
        attributionControl={true}
        style={{ width: '100%', height: '100%' }}
      >
        {/* All four corners are taken by floating UI in Block A:
            top-left InfoCard, top-right TopBarFloating, bottom-left FabCluster,
            bottom-right SpeedControl. Native NavigationControl is suppressed —
            scroll/pinch zoom and right-click drag (pitch) remain available.
            Phase 6 polish may reintroduce a custom zoom widget. */}
        {children}
      </Map>
    </div>
  );
});
